/**
 * Admin Marketing — Discovery API. `/api/admin/v2/marketing/discovery*`
 *
 * Candidate places, events and experiences found in what tracked sources post.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  ApiDiscoveryCandidate,
  ApiDiscoveryList,
  DiscoveryStatus,
  MarketingListParams,
} from '@/client/types/marketing/api';
import { MARKETING_BASE } from './keys';

const discoveryKey = ['marketing', 'discovery'] as const;

export const useDiscoveryQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiDiscoveryList>>({
    defaultErrorMessage: 'Could not load discovery candidates',
    endpoint: `${MARKETING_BASE}/discovery`,
  });
  return useQuery({
    queryKey: [...discoveryKey, params],
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

/** PATCH body: status, eid (set when a listing was created), note. */
export const useUpdateDiscoveryMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { id: string; status?: DiscoveryStatus; eid?: number; note?: string },
    AdminEnvelope<ApiDiscoveryCandidate>
  >({
    defaultErrorMessage: 'Could not update the candidate',
    endpoint: (p) => `${MARKETING_BASE}/discovery/${p.id}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: discoveryKey }),
  });
};

/** Creates the place's listing (REVIEW) from the cached Google record; never a paid search. */
export const useImportDiscoveryMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ id: string }, AdminEnvelope<ApiDiscoveryCandidate>>({
    defaultErrorMessage: 'Could not import the place',
    endpoint: (p) => `${MARKETING_BASE}/discovery/${p.id}/import`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: discoveryKey }),
  });
};

/** One paid Google Maps search (SearchAPI, daily-capped server-side), then import on a clear match. */
export const useSearchDiscoveryMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ id: string; confirm: true }, AdminEnvelope<ApiDiscoveryCandidate>>({
    defaultErrorMessage: 'Could not search for the place',
    endpoint: (p) => `${MARKETING_BASE}/discovery/${p.id}/search`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: discoveryKey }),
  });
};

export const useRunDiscoveryMutation = () => {
  const mutationFn = useGetMutationFn<
    Record<string, never>,
    AdminEnvelope<{ queued: boolean; task_id?: string | null }>
  >({
    defaultErrorMessage: 'Could not start discovery',
    endpoint: `${MARKETING_BASE}/discovery/run`,
    method: 'POST',
  });
  return useMutation({ mutationFn });
};
